<script>
import {VclCode} from 'vue-content-loading';
import {OrbitSpinner} from 'epic-spinners'
import {get_acsrf_token} from "@/methods";
import {required, minLength, maxLength} from "vuelidate/lib/validators";
import CopyableInput from "@/components/CopyableInput";
import CopyableText from "../../CopyableText.vue";

function parseISO8601String(dateString) {
	var timebits = /^([0-9]{4})-([0-9]{2})-([0-9]{2})T([0-9]{2}):([0-9]{2})(?::([0-9]*)(\.[0-9]*)?)?(?:([+-])([0-9]{2})([0-9]{2}))?/;
	var m = timebits.exec(dateString);
	var resultDate;
	if (m) {
		var utcdate = Date.UTC(parseInt(m[1]),
				parseInt(m[2]) - 1, // months are zero-offset (!)
				parseInt(m[3]),
				parseInt(m[4]), parseInt(m[5]), // hh:mm
				(m[6] && parseInt(m[6]) || 0),  // optional seconds
				(m[7] && parseFloat(m[7]) * 1000) || 0); // optional fraction
		// utcdate is milliseconds since the epoch
		if (m[9] && m[10]) {
			var offsetMinutes = parseInt(m[9]) * 60 + parseInt(m[10]);
			utcdate += (m[8] === '+' ? -1 : +1) * offsetMinutes * 60000;
		}
		resultDate = new Date(utcdate);
	} else {
		resultDate = null;
	}
	return resultDate;
}

export default {
	props: ['application_id'],
	components: {
		CopyableText,
		// eslint-disable-next-line vue/no-unused-components
		VclCode,
		CopyableInput,
		OrbitSpinner
	},
	validations: {
		application: {
			name: {required, minLength: minLength(3), maxLength: maxLength(24)}
		}
	},
	methods: {
		handleError: function (error) {
			console.log(`[ERROR] ${error}`);
			this.error = true;
		},
		getData: function () {
			fetch(process.env.VUE_APP_ROOT_API + `v1/api/${this.application_id}`, {credentials: 'include'})
			.then(response => {
				if (response.ok) {
					return response.json();
				} else {
					throw new Error(`(${this.$vnode.componentOptions.tag}) Failed with API error ${response.status}=${response.statusText} (${response.url})`);
				}
			})
			.then(data => {
				this.ready = true;
				this.application = data.application;
				this.chartData = data.charts;

				this.$nextTick(() => {
					this.$refs.requestCountChart.updateOptions({
						xaxis: {
							categories: this.chartData.requests.labels
						}
					})
					this.$refs.requestCountChart.updateSeries(this.chartData.requests.series);
				})
			})
			.catch(error => {
				this.handleError(error);
			});
		},
		async validateApplicationForm() {
			this.$v.application.$touch();
		},
		async deleteApplication() {
			this.inProgress = true;
			this.deleting = true;

			let result = await this.$swal({
				icon: 'warning',
				text: 'Once you delete an API application, there is no going back. Please be certain.',
				title: `Delete Data API Application`,
				showCancelButton: true,
				confirmButtonText: `Delete ${this.application.name}`,
				confirmButtonColor: '#c3cbe4',
				cancelButtonColor: '#f46a6a',
			});
			if (!result.isConfirmed) {
				this.inProgress = false;
				this.deleting = false;
				return;
			}

			try {
				let acsrf_token = await get_acsrf_token();
				let payload = {
					acsrf_token: acsrf_token
				};
				let response = await fetch(process.env.VUE_APP_ROOT_API + `v1/api/${this.application_id}`, {
					method: 'DELETE',
					body: JSON.stringify(payload),
					credentials: 'include'
				});

				if (response.ok) {
					this.$toast.warning('Application deleted');
					await this.$router.push({name: 'applications'});
				} else {
					if (response.status === 429) {
						this.$toast.warning('You have been rate limited');

					} else
						throw new Error(`(${this.$vnode.componentOptions.tag}) Failed with API error ${response.status}=${response.statusText} (${response.url})`);
				}
			} catch (error) {
				console.log(`[ERROR] ${error}`);
				this.$swal({
					icon: 'error',
					text: 'Request failed to complete'
				});
			}
			this.deleting = false;
			this.inProgress = false;
		},
		async saveApplication() {
			this.$v.application.$touch();
			if (this.$v.application.$error) return;

			this.inProgress = true;
			this.saving = true;
			await new Promise(resolve => setTimeout(resolve, 2000));
			try {
				let acsrf_token = await get_acsrf_token();
				let payload = {
					acsrf_token: acsrf_token,
					name: this.application.name
				};
				let response = await fetch(process.env.VUE_APP_ROOT_API + `v1/api/${this.application_id}`, {
					method: 'PATCH',
					body: JSON.stringify(payload),
					credentials: 'include'
				});

				if (response.ok) {
					this.$toast.success('Changes saved');
					this.saved = true;
					setTimeout(() => {
						this.saved = false;
					}, 2000);
				} else {
					if (response.status === 429) {
						this.$toast.warning('You have been rate limited');

					} else
						throw new Error(`(${this.$vnode.componentOptions.tag}) Failed with API error ${response.status}=${response.statusText} (${response.url})`);
				}
			} catch (error) {
				console.log(`[ERROR] ${error}`);
				this.$swal({
					icon: 'error',
					text: 'Update failed'
				});
			}
			this.saving = false;
			this.inProgress = false;
		}
	},
	created() {

	},
	mounted() {
		this.getData();
	},
	data() {
		return {
			ready: false,
			error: false,
			submitted: false,
			application: {
				name: null
			},
			chartData: null,
			showSecret: false,
			inProgress: false,
			saving: false,
			saved: false,
			deleting: false,
			charts: {
				requests: {
					series: [],
					chart: {
						noData: {
							text: 'Loading...'
						},
						chart: {
							height: 320,
							type: 'line',
							toolbar: 'false',
							dropShadow: {
								enabled: true,
								color: '#000',
								top: 18,
								left: 7,
								blur: 8,
								opacity: 0.2
							},
						},
						dataLabels: {
							enabled: false
						},
						colors: ['#556ee6', '#f46a6a'],
						stroke: {
							curve: 'smooth',
							width: 3,
						},
						xaxis: {
							type: 'category',
							categories: []
						}
					}
				}
			}
		}
	}
};
</script>

<template>
	<div>
		<template v-if="ready">
			<template v-if="!application">
				<div class="row">
					<div class="col justify-content-center">
						<div class="info-component text-center align-middle mt-auto mb-auto">
							<div class="info-component-space">
								<i class="fad fa-empty-set info-component-icon"></i>
							</div>
							<div class="row" style="margin-top: 20px;">
								<div class="col-lg-12">
									<h3 v-b-tooltip.hover title="Wow such empty 👻">Nothing here yet</h3>
									<h5 class="text-muted">Start by creating a new application</h5>
								</div>
							</div>
						</div>
					</div>
				</div>
			</template>
			<template v-else>
				<div class="row mb-0 pb-0">
					<div class="col">
						<div class="card mini-stats-wid mb-0 pb-0">
							<div class="card-body" style="height: 6rem;">
								<div class="d-flex flex-wrap">
									<div class="me-3">
										<h6 class="text-muted mb-2 text-uppercase"> Application</h6>
										<h5 class="mb-0">
											<span class="mr-2">
												<CopyableText size="lg" :text="application.name" label="Application Name"/>
											</span>
											<CopyableText size="md" class="text-muted" :text="application.id" label="Application Id"/>
										</h5>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div class="col">
						<div class="card mini-stats-wid mb-0 pb-0">
							<div class="card-body" style="height: 6rem;">
								<div class="d-flex flex-fill w-100">
									<div class="me-3 w-100">
										<h6 class="text-muted mb-2 text-uppercase"> Application Secret </h6>
										<h5 class="mb-0">
											<template v-if="!showSecret">
												<button class="btn btn-primary btn-sm" v-on:click="showSecret = true">
													Reveal secret
												</button>
											</template>
											<template v-else>
												<CopyableInput size="md" class="text-code w-100" :text="application.secret" label="Application secret"/>
											</template>
										</h5>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div class="col">
						<div class="card mini-stats-wid mb-0 pb-0">
							<div class="card-body" style="height: 6rem;">
								<div class="d-flex flex-fill w-100">
									<div class="me-3 w-100">
										<h6 class="text-muted mb-2 text-uppercase"> Grant URL </h6>
										<h5 class="mb-0">
											<CopyableInput size="md" class="w-100" :text="'https://app.cftools.cloud/authorize/'+application.id" label="Grant URL"/>
										</h5>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div class="row mt-4">
					<div class="col-lg-12 col-sm-12">
						<div class="card card-body">
							<h6 class="text-muted mb-2 text-uppercase">
								Request Statistics
								<template v-if="(new Date().getFullYear() === 2023) || (new Date().getFullYear() <= 2024 && new Date().getMonth() <= 8)">
									<span class="badge badge-info text-black font-weight-bolder">
										Statistics collection since 08.2023
									</span>
								</template>
							</h6>
							<div class="row">
								<div class="col-lg-3 col-sm-6">
									<div class="card mini-stats-wid mb-0 pb-0 border">
										<div class="card-body" style="height: 5.5rem;">
											<div class="d-flex flex-wrap">
												<div class="me-3">
													<p class="text-muted mb-2 text-uppercase">
														Request Count (Year)
													</p>
													<h5 class="mb-0">
														{{ $n(this.chartData.requests.year) }}
													</h5>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div class="col-lg-3 col-sm-6">
									<div class="card mini-stats-wid mb-0 pb-0 border">
										<div class="card-body" style="height: 5.5rem;">
											<div class="d-flex flex-wrap">
												<div class="me-3">
													<p class="text-muted mb-2 text-uppercase">
														Request Count (Month)
													</p>
													<h5 class="mb-0">
														{{ $n(this.chartData.requests.month) }}
													</h5>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
							<div class="mr-4">
								<apexchart
										ref="requestCountChart"
										class="apex-charts"
										dir="ltr"
										height="320"
										:series="charts.requests.series"
										:options="charts.requests.chart"
								></apexchart>
							</div>
						</div>
					</div>
				</div>
				<div class="row mt-2">
					<div class="col">
						<b-card no-body class="settings-collapse hard-border">
							<a v-b-toggle.accordion-name class="text-dark" href="javascript: void(0);">
								<b-card-header header-tag="header" role="tab">
									<h3 class="m-0 d-inline-flex">
										Application Name
									</h3>
									<div class="d-inline-flex float-right">
										<div class="btn btn-sm btn-dark">
											Expand
										</div>
									</div>
								</b-card-header>
							</a>
							<b-collapse id="accordion-name" accordion="settings" role="tabpanel">
								<b-card-body>
									<b-card-text>
										<div class="row">
											<div class="w-50">
												<div class="ml-4 mr-4">
													<input
															type="text"
															maxlength="24"
															:value="application.name"
															class="form-control form-control form-control-lg"
															:class="{ 'is-invalid': $v.application.name.$error, 'is-valid': saved }"
															v-on:change="validateApplicationForm()"
															v-on:input="validateApplicationForm()"
													/>
													<div v-if="$v.application.name.$error" class="invalid-feedback">
														<span v-if="!$v.application.name.required">Required</span>
														<span v-if="!$v.application.name.minLength || !$v.application.name.maxLength">3 - 24 characters</span>
													</div>
												</div>
											</div>
											<div class="w-25">
											</div>
											<div class="w-25">
												<div style="text-align: center;">
													<button class="btn btn-success btn-block" :disabled="inProgress" v-on:click="saveApplication()">
														<template v-if="saving">
															<orbit-spinner
																	:animation-duration="1200"
																	:size="22"
																	class="align-middle d-inline-block text-success"
															/>
														</template>
														<template v-else>
															Save changes
														</template>
													</button>
												</div>
											</div>
										</div>
									</b-card-text>
								</b-card-body>
							</b-collapse>
						</b-card>

						<b-card no-body class="settings-collapse border border-danger hard-border">
							<a v-b-toggle.accordion-dangerzone class="text-dark" href="javascript: void(0);">
								<b-card-header header-tag="header" role="tab">
									<h3 class="m-0 d-inline-flex text-danger">
										Dangerzone
									</h3>
									<div class="d-inline-flex float-right">
										<div class="btn btn-sm btn-dark">
											Expand
										</div>
									</div>
								</b-card-header>
							</a>
							<b-collapse id="accordion-dangerzone" accordion="settings" role="tabpanel">
								<b-card-body>
									<b-card-text>
										<div class="row">
											<div class="w-75">
												<div class="ml-4 mr-4">
													<h4 class="text-danger">
														Delete Application
													</h4>
													<span>
                            Delete this Data API application and all entities associated. This is irreversible.
                          </span>
												</div>
											</div>
											<div class="w-25">
												<div style="text-align: center;">
													<button class="btn btn-danger btn-block" :disabled="inProgress" v-on:click="deleteApplication()">
														<template v-if="deleting">
															<orbit-spinner
																	:animation-duration="1200"
																	:size="22"
																	class="align-middle d-inline-block text-danger"
															/>
														</template>
														<template v-else>
															Delete
														</template>
													</button>
												</div>
											</div>
										</div>
									</b-card-text>
								</b-card-body>
							</b-collapse>
						</b-card>
					</div>
				</div>
			</template>
		</template>
		<template v-else-if="error">
			<div class="row">
				<div class="col-lg-12 col-sm-12 row justify-content-center">
					<div class="col-lg-6 col-sm-12">
						<div class="card border border-danger">
							<div class="card-header bg-transparent border-danger">
								<h5 class="my-0 text-danger">
									<i class="far fa-exclamation-circle mr-3"></i>
									We have encountered an error
								</h5>
							</div>
							<div class="card-body pt-0">
								<h5 class="card-title mt-0">Please try again later</h5>
							</div>
						</div>
					</div>
				</div>
			</div>
		</template>
		<template v-else>
			<div class="row">
				<div class="col-lg-3 col-sm-12" v-for="index in 4" :key="index">
					<div class="card">
						<div class="card-body">
							<vcl-code :width="300" :speed="1" primary="#FFFFFF" secondary="#bdbdbd"></vcl-code>
						</div>
					</div>
				</div>
			</div>
		</template>
	</div>

</template>
